import { useId } from "react";

/**
 * The four-point star from lucide's `Sparkles`. Reused for the satellite: a
 * scaled-down FILLED copy instead of lucide's thin `+` strokes, which vanish
 * into antialiasing at row/banner sizes.
 */
const STAR_PATH =
  "M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z";

/**
 * The app-wide suggestion mark, stroked with the same purple→blue identity
 * used in `ai-assistant-card.tsx` (`from-purple-500 to-blue-500`). Static by
 * design. The filled satellite star is what visually separates it from
 * Gemini's lone gradient star — it must stay readable at every size this is
 * rendered at. See docs/ai-suggestion-ui.md.
 */
export interface AiSparkleIconProps {
  /** Width/height in pixels (viewBox is square). */
  size?: number;
  className?: string;
}

export function AiSparkleIcon({ size = 14, className }: AiSparkleIconProps) {
  // Unique per instance: SVG <defs> ids are document-global, so two icons on
  // the same page (e.g. a banner and several rows) would otherwise collide
  // on one <linearGradient>.
  const gradientId = useId();

  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={`url(#${gradientId})`}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`shrink-0 ${className ?? ""}`}
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#a855f7" />
          <stop offset="100%" stopColor="#3b82f6" />
        </linearGradient>
      </defs>
      <path d={STAR_PATH} />
      <g transform="translate(16.3 0.2) scale(0.28)">
        <path d={STAR_PATH} fill={`url(#${gradientId})`} stroke="none" />
      </g>
    </svg>
  );
}
